/* eslint-disable indent */
import { toLocalDateTimeWithSeconds } from '@src/main/shared/utils/dateUtils';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { Button } from 'primereact/button';
import { ColumnFilterElementTemplateOptions } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import { ReactNode } from 'react';
import './IncidentTable.module.scss';

type BaseColumnOptions = {
  field: string;
  labelKey: string;
  minWidth?: string;
  isDate?: boolean;
  dateOnly?: boolean;
  dateFilterTemplate?: (options: ColumnFilterElementTemplateOptions) => ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bodyTemplate?: (rowData: any) => ReactNode;
  sortable?: boolean;
  filter?: boolean;
};
type State = 'Nuevo' | 'En curso' | 'En espera' | 'Resuelto' | 'Cerrado' | 'Cancelado';

type IncidentRow = {
  linkColumbus?: string;
  state: State;
  idEmployeeAnswered?: string;
  fullNameAnswered?: string;
  dateAnswered?: string;
  incidentImage?: string;
  comment?: string;
  [key: string]: unknown;
  categorization?: string;
  message: string;
};

// Date-only template (DD/MM/YYYY)
const makeDateOnlyBodyTemplate = (field: string) => (rowData: Record<string, unknown>) => {
  const dateValue = rowData[field] as Date | string | undefined;
  const date = dateValue ? new Date(dateValue) : null;
  return !date || Number.isNaN(date.getTime()) ? undefined : format(date, 'dd/MM/yyyy');
};

// Date-time template (DD/MM/YYYY HH:mm)
const makeDateTimeBodyTemplate = (field: string) => (rowData: Record<string, unknown>) => {
  const dateValue = rowData[field] as Date | string | undefined;
  const date = dateValue ? new Date(dateValue) : null;
  return !date || Number.isNaN(date.getTime()) ? undefined : format(date, 'dd/MM/yyyy HH:mm');
};

const createBaseColumn = (
  t: TFunction,
  {
    field,
    labelKey,
    minWidth = '10rem',
    isDate = false,
    dateOnly = false,
    dateFilterTemplate,
    bodyTemplate,
    sortable = true,
    filter = true,
  }: BaseColumnOptions,
) => {
  const base: Record<string, unknown> = {
    field,
    header: t(`${labelKey}`),
    filter,
    filterField: field,
    showFilterOperator: false,
    showAddButton: false,
    showFilterMatchModes: false,
    style: { minWidth },
    sortable,
  };

  if (isDate) {
    base.dataType = 'date';
    base.body =
      bodyTemplate ??
      (dateOnly ? makeDateOnlyBodyTemplate(field) : makeDateTimeBodyTemplate(field));
    base.filterElement = dateFilterTemplate;
  }

  if (bodyTemplate && !isDate) {
    base.body = bodyTemplate;
  }

  return base;
};

export const getFlightDateColumnProps = (
  t: TFunction,
  dateFilterTemplate?: (options: ColumnFilterElementTemplateOptions) => ReactNode,
) =>
  createBaseColumn(t, {
    field: 'flightDate',
    labelKey: t('otherIncidentManagement.columnsName.flightDate'),
    minWidth: '10rem',
    isDate: true,
    dateFilterTemplate,

    bodyTemplate: (row) => {
      const date = new Date(row.flightDate);
      return (
        <span>
          <div>{date.toLocaleDateString()}</div>
        </span>
      );
    },
  });

export const getIncidentDateColumnProps = (
  t: TFunction,
  dateFilterTemplate?: (options: ColumnFilterElementTemplateOptions) => ReactNode,
) =>
  createBaseColumn(t, {
    field: 'incidentDate',
    labelKey: t('otherIncidentManagement.columnsName.incidentDate'),
    minWidth: '12rem',
    isDate: true,
    dateFilterTemplate,
    bodyTemplate: (row) => {
      const date = new Date(row.incidentDate);
      return (
        <span>
          <div>{date.toLocaleDateString()}</div>
        </span>
      );
    },
  });

export const getFlightNumberColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'flightNumber',
    labelKey: t('otherIncidentManagement.columnsName.flightNumber'),
    minWidth: '8rem',
  });

export const getDepartureColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'departure',
    labelKey: t('otherIncidentManagement.columnsName.departure'),
    minWidth: '8rem',
  });

export const getArrivalColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'arrival',
    labelKey: t('otherIncidentManagement.columnsName.arrival'),
    minWidth: '8rem',
  });

export const getEmployeeIdColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'employeeId',
    labelKey: t('otherIncidentManagement.columnsName.employeeId'),
    minWidth: '8rem',
    bodyTemplate: (rowData) => (
      <span title={`${rowData.employeeName} (${rowData.employeeRole})`}>{rowData.employeeId}</span>
    ),
  });

export const getAppVersionColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'appVersion',
    labelKey: t('otherIncidentManagement.columnsName.appVersion'),
    minWidth: '6rem',
  });

export const getDescriptionColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'comment',
    labelKey: t('otherIncidentManagement.columnsName.description'),
    filter: true,
    sortable: true,
    bodyTemplate: (rowData: IncidentRow) => {
      const categorization = rowData.categorization || '';
      const comment = rowData.comment || '';

      const truncated = comment.length > 50 ? `${comment.slice(0, 50)}...` : comment;

      return (
        <>
          <Tooltip target=".description-icon" className="description  -tooltip" />
          <span className="description-icon" data-pr-tooltip={comment}>
            {categorization ? `[${categorization}] ` : ''}
            {truncated}
          </span>
        </>
      );
    },
  });

export const getAnsweredColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'idEmployeeAnswered',
    labelKey: t('otherIncidentManagement.columnsName.answered'),
    minWidth: '6rem',
    filter: false,
    sortable: true,
    bodyTemplate: (rowData: IncidentRow) => {
      const answered = rowData.idEmployeeAnswered;

      const dateAnswered = rowData.dateAnswered
        ? toLocalDateTimeWithSeconds(new Date(rowData.dateAnswered))
        : '';

      if (!answered) {
        return null;
      }

      return (
        <div
          style={{
            minWidth: '10rem',
            textAlign: 'center',
          }}
        >
          <Tooltip target=".answered-icon" className="answered-tooltip" autoHide={false} />

          <span
            className="answered-icon"
            data-pr-tooltip={`${rowData.fullNameAnswered}  ${dateAnswered} 
             Respuesta:  ${rowData.message ?? ''} `}
          >
            <i
              className="pi pi-check"
              style={{
                cursor: 'default',
              }}
            />
          </span>
        </div>
      );
    },
  });

export const getImageColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: '',
    labelKey: t('otherIncidentManagement.columnsName.image'),
    minWidth: '6rem',
    filter: false,
    sortable: false,
    bodyTemplate: (rowData: IncidentRow) => {
      const answered = rowData.incidentImage;
      if (answered) {
        return (
          <Button
            disabled
            icon="pi pi-check"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
            }}
          />
        );
      }
      return null;
    },
  });
export const getAcctionsColumnProps = (t: TFunction, handleOpenModal: (row: IncidentRow) => void) =>
  createBaseColumn(t, {
    field: 'state',
    labelKey: t('otherIncidentManagement.columnsName.state'),
    sortable: true,
    filter: true,
    bodyTemplate: (rowData: IncidentRow) => {
      const link = rowData.linkColumbus;

      if (link) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              onClick={() => handleOpenModal(rowData)}
              icon="pi pi-eye"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            />
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline' }}
            >
              {`${rowData.incidentColumbusNumber}`}
            </a>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            onClick={() => handleOpenModal(rowData)}
            icon="pi pi-eye"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            title={t('otherIncidentManagement.bottomMenu.view')}
          />
        </div>
      );
    },
  });
